import { inferBalanceSheetCategory } from '../data/balanceSheetCategories';

const CONTRIB_KEYS = ['contribRothIra', 'contribHsa', 'contrib401k', 'contribBrokerage'];

const BROKERAGE_CATS = new Set(['robinhood', 'fundrise']);
const TAX_ADV_CATS = new Set(['roth401k', 'traditionalIra', 'rothIra', 'hsa']);
const CASH_CATS = new Set(['cashOnHand', 'checking', 'savings', 'tBills']);
const PROPERTY_CATS = new Set(['vehicle']);
const OTHER_CATS = new Set(['lifeInsurance', 'loyaltyPoints', 'accountsReceivable', 'securityDeposit', 'ownershiftEquity', 'collectables']);
const CREDIT_CATS = new Set(['creditCards', 'loansFromFriends']);
const LOAN_CATS = new Set(['vehicleLoan']);

/**
 * Build a balance_sheet_monthly snapshot from current store state.
 * Preserves existing contribution data for the month.
 *
 * Investment categories sum from the `investments` holdings table (matching
 * Dashboard / BalanceSheetMonthly live calc), falling back to account balance
 * only for investment accounts with no matching holdings. Using raw
 * account.balance for investment accounts caused the snapshot to diverge from
 * the live KPI when account-level balances drifted from holdings sums.
 */
export function buildMonthSnapshot(monthKey, { accounts, creditCards, investments, balanceSheetMonthly, userSettings }) {
  const hiddenAccounts = userSettings?.hiddenAccounts || [];
  const visibleAccounts = accounts.filter((a) => !hiddenAccounts.includes(a.id));

  const sums = {
    cashOnHand: 0, checking: 0, savings: 0, tBills: 0,
    robinhood: 0, fundrise: 0,
    roth401k: 0, traditionalIra: 0, rothIra: 0, hsa: 0,
    vehicle: 0,
    lifeInsurance: 0, loyaltyPoints: 0, accountsReceivable: 0,
    securityDeposit: 0, ownershiftEquity: 0,
    creditCards: 0, loansFromFriends: 0, vehicleLoan: 0,
  };

  // ── Computed values (include ALL credit cards — hidden cards still count) ──
  const loyaltyPointsValue = creditCards
    .reduce((sum, c) => sum + (c.pointsBalance || 0), 0) * 0.01;

  // ── Non-investment accounts ──
  visibleAccounts
    .filter((a) => a.type !== 'investment')
    .forEach((a) => {
      const balance = /est\.?\s*loyalty/i.test(a.name) ? loyaltyPointsValue : (a.balance || 0);
      const cat = a.balanceSheetCategory || inferBalanceSheetCategory(a);
      if (cat && cat in sums) {
        if (a.type === 'loan') sums[cat] += Math.abs(balance);
        else sums[cat] += balance;
      }
    });

  // ── Investment accounts: holdings sum by account, fall back to account balance ──
  const holdingsByAccount = {};
  (investments || []).forEach((inv) => {
    const acct = inv.account || 'Unassigned';
    holdingsByAccount[acct] = (holdingsByAccount[acct] || 0) + (inv.currentValue || 0);
  });

  Object.entries(holdingsByAccount).forEach(([holdingAcctName, total]) => {
    const lower = holdingAcctName.toLowerCase();
    const matchedAcct = visibleAccounts.find(
      (a) => a.type === 'investment' && (
        (a.name || '').toLowerCase().includes(lower) ||
        lower.includes((a.name || '').toLowerCase())
      ),
    );
    const cat = matchedAcct
      ? (matchedAcct.balanceSheetCategory || inferBalanceSheetCategory(matchedAcct))
      : inferBalanceSheetCategory({ type: 'investment', name: holdingAcctName });
    if (cat && cat in sums) sums[cat] += total;
  });

  const holdingNames = Object.keys(holdingsByAccount).map((n) => n.toLowerCase());
  visibleAccounts
    .filter((a) => a.type === 'investment' && (a.balance || 0) !== 0)
    .filter((a) => !holdingNames.some((h) =>
      (a.name || '').toLowerCase().includes(h) || h.includes((a.name || '').toLowerCase()),
    ))
    .forEach((a) => {
      const cat = a.balanceSheetCategory || inferBalanceSheetCategory(a);
      if (cat && cat in sums) sums[cat] += (a.balance || 0);
    });

  // ── Credit cards (sum absolute balances — hidden cards still count) ──
  sums.creditCards = creditCards
    .reduce((sum, c) => sum + Math.abs(c.balance || 0), 0);

  // ── Subtotals ──
  const cashTotal = Object.entries(sums).filter(([k]) => CASH_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const brokerageTotal = Object.entries(sums).filter(([k]) => BROKERAGE_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const taxAdvTotal = Object.entries(sums).filter(([k]) => TAX_ADV_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const investmentsTotal = brokerageTotal + taxAdvTotal;
  const propertyTotal = Object.entries(sums).filter(([k]) => PROPERTY_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const otherAssetsTotal = Object.entries(sums).filter(([k]) => OTHER_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const totalAssets = cashTotal + investmentsTotal + propertyTotal + otherAssetsTotal;

  const ccTotal = Object.entries(sums).filter(([k]) => CREDIT_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const loansTotal = Object.entries(sums).filter(([k]) => LOAN_CATS.has(k)).reduce((s, [, v]) => s + v, 0);
  const totalLiabilities = ccTotal + loansTotal;
  const netWorth = totalAssets - totalLiabilities;

  // ── Preserve existing contribution data ──
  const existing = (balanceSheetMonthly || []).find((r) => r.month === monthKey) || {};
  const contribs = {};
  CONTRIB_KEYS.forEach((k) => { contribs[k] = existing[k] || 0; });

  return {
    month: monthKey,
    ...sums,
    ...contribs,
    totalAssets,
    totalLiabilities,
    netWorth,
  };
}
